/* ============================================================
   PDP  –  pdp-measure.js: solskydd's measurement extras.

   Loaded before pdp.js by pdp-product-template.liquid when the section
   passes measure or picker_rows (main-product-solskydd), and called by
   pdp.js's init() right after the pills are measured.

   One control differs from the old page and picks the theme's own radios:
   a height span chosen from the typed height instead of a row of pills (a
   long fixed-size row as a dropdown, the other one, is optionSelects in
   pdp.js, which skenor-stanger shares). A span switch
   rebuilds the customizer, and around it heightSpans is the only place
   these files touch its fields, each time as a shopper could: width and
   height are emptied just before the switch, its text and number fields
   are inert during it, and the shopper's entries - with the keys typed
   meanwhile - are typed back afterwards. Its fields grid also holds one
   line of ours (measureNotes) that says why a measurement cannot be
   ordered.
   ============================================================ */

(function () {
  'use strict';

  var PDP = window.HastaPDP = window.HastaPDP || {};

  // a field's text after keys pressed while it could not take them ("\b"
  // deletes): a number field takes digits and one decimal separator,
  // written "." as it holds it; a text field takes any character
  function typeInto(text, keys, number) {
    var out = String(text || '');
    keys.forEach(function (k) {
      if (k === '\b') { out = out.slice(0, -1); return; }
      if (!number) { out += k; return; }
      if (/^[0-9]$/.test(k)) out += k;
      else if ((k === ',' || k === '.') && out.indexOf('.') === -1) out += '.';
    });
    return out;
  }

  /* ---------- what cannot be ordered, in words -----------------
     The customizer rejects a measurement with a red edge and a locked buy
     button and says nothing more. This page adds states the old one never
     had - heightSpans below types a width back into a span that may allow
     less of it - so one line of ours under the width and height says why
     what is typed cannot be ordered, in the section's own words (its
     "Måttfält" settings, printed by pdp-product-template.liquid as
     data-pdp-messages). On every made-to-measure product:
     - the height: on a product sold in height spans heightSpans says it
       (outside the colour's range, between two spans, a span the colour
       lacks, a failed update); elsewhere the field's own min and max
     - the width, against the field's own min and max. Where a colour's
       spans differ in width (Mellanglaspersienn: 200 cm up to 120 cm high,
       100 cm at 201-220) the text names the height it depends on
     - valid measurements the customizer finds no price for (a data error):
       its button stays locked, and this is the only word about it
     An empty field is not a problem, only unfinished: its hint says what
     goes in it.
     The rules are the customizer's, read and copied here, never called
     (validateInputs in component-customizer.js): a number above 0 within
     the input's min and max. Its fields, prices and events are left alone.
     The line is a <p> of ours in its fields grid, with its own class and
     no id, which its observer (it looks for ids with "customizer") passes
     by. It is always there as a live region, empty when all is well - a
     region added or shown together with its text often goes unannounced -
     and it is put back, empty, each time the theme rebuilds the fields. */
  function measureNotes(root) {
    var id = root.id;
    var texts = {};
    var source = root.querySelector('[data-pdp-messages]');
    try { texts = source ? JSON.parse(source.textContent) || {} : {}; } catch (e) { texts = {}; }
    var FIELDS = ['max-width-', 'max-height-', 'mounting-height-'];
    // the theme locks the buy button 100 ms after a rebuild and the new
    // customizer judges it 100 ms after that (global.js
    // checkCustomizerFields, component-customizer.js setupEventListeners):
    // until then the button says nothing about the price
    var AFTER_REBUILD = 250;
    var parts = { height: '', width: '', price: '' };
    var span = null;          // heightSpans' hooks, on a product sold in height spans
    var priceTimer = null;
    var rebuiltAt = 0;

    function input(prefix) { return document.getElementById(prefix + id); }

    // numbers the Swedish way: 80,5, and no ",0"
    function num(n) {
      return typeof n === 'number' && !isNaN(n) ? String(Math.round(n * 100) / 100).replace('.', ',') : '';
    }

    // a text from the settings with its [placeholders] filled in
    function format(kind, values) {
      var text = String(texts[kind] || '');
      Object.keys(values || {}).forEach(function (k) {
        var v = values[k];
        var shown = typeof v === 'number' ? num(v) : (v === null || v === undefined ? '' : String(v));
        text = text.split('[' + k + ']').join(shown);
      });
      return text;
    }

    // the customizer's own test (validateInputs, read): a number above 0
    // within the input's min and max. null for no field or an empty one
    function check(field) {
      if (!field) return null;
      var text = String(field.value || '').trim();
      if (!text) return null;
      var value = parseFloat(text);
      var min = parseFloat(field.getAttribute('min')) || 0;
      var max = parseFloat(field.getAttribute('max')) || 999999;
      return { ok: !isNaN(value) && value >= min && value <= max && value > 0, value: value, min: min, max: max, text: text };
    }

    // a whole number that more digits could still make valid - "3" on the
    // way to 35. A field without a max reads 999999, so there every whole
    // number may grow and the line waits until the field is left; the red
    // edge still comes at once, from the customizer
    function mayGrow(c) {
      return /^\d+$/.test(c.text) && Number(c.text) * 10 <= c.max;
    }

    function typing(e, prefix) {
      return !!e && e.type === 'input' && e.target === input(prefix);
    }

    function widthPart(e) {
      var w = check(input('max-width-'));
      if (!w || w.ok || (typing(e, 'max-width-') && mayGrow(w))) return '';
      var h = check(input('max-height-'));
      if (span && h && h.ok && span.widthVaries()) {
        return format('width_height', { height: h.value, min: w.min, max: w.max });
      }
      return format('width', { min: w.min, max: w.max });
    }

    // on a product sold in height spans, heightSpans says it instead
    function heightPart(e) {
      if (span) return parts.height;
      var h = check(input('max-height-'));
      if (!h || h.ok || (typing(e, 'max-height-') && mayGrow(h))) return '';
      return format('range', { min: h.min, max: h.max });
    }

    // Valid measurements and still no price: every field filled, width and
    // height within their ranges, the buy button locked, and the hidden
    // total empty or 0 (hasValidPriceInformation, read). Each of these alone
    // also happens for other reasons - the lock after every rebuild, an
    // empty total where the customizer prices nothing - so all of them.
    // Read after the customizer's own handlers have run.
    function pricePart() {
      if (span && span.busy()) return '';
      if (Date.now() - rebuiltAt < AFTER_REBUILD) return '';
      var present = FIELDS.map(function (prefix) { return input(prefix); }).filter(Boolean);
      if (!present.length) return '';
      for (var i = 0; i < present.length; i++) {
        if (!String(present[i].value || '').trim()) return '';
        // any mounting height is valid to the customizer, text included
        if (present[i] !== input('mounting-height-') && !check(present[i]).ok) return '';
      }
      var form = input('product-form-');
      var button = form && form.querySelector('[name="add"]');
      var total = input('custom-total-price-');
      if (!button || !button.disabled || !total) return '';
      return parseFloat(String(total.value).replace(',', '.')) > 0 ? '' : format('price', {});
    }

    // under the height, or under the width where there is no height field
    function line() {
      var anchor = input('max-height-') || input('max-width-');
      var cell = anchor && anchor.closest('.field');
      if (!cell || !cell.parentNode) return null;
      var el = cell.parentNode.querySelector(':scope > .rd-span-msg');
      if (!el) {
        el = document.createElement('p');
        el.className = 'rd-span-msg';
        el.setAttribute('role', 'status');
        cell.parentNode.insertBefore(el, cell.nextSibling);
      }
      return el;
    }

    function paint() {
      var el = line();
      if (!el) return;
      var text = [parts.height, parts.width, parts.price].filter(Boolean).join(' ');
      if (el.textContent !== text) el.textContent = text;
    }

    function judge(e) {
      parts.width = widthPart(e);
      parts.height = heightPart(e);
      paint();
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () {
        parts.price = pricePart();
        paint();
      }, 0);
    }

    function reset() {
      clearTimeout(priceTimer);
      parts = { height: '', width: '', price: '' };
      paint();
    }

    function onEvent(e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT' || !FIELDS.some(function (prefix) { return t.id === prefix + id; })) return;
      // a span switch empties the fields and types them back: judged then
      if (span && span.busy()) return;
      judge(e);
    }

    return {
      format: format,
      // heightSpans: its hooks (busy, widthVaries), and its word on the height
      attach: function (hooks) { span = hooks; },
      height: function (text) { parts.height = text || ''; paint(); },
      refresh: function () { judge(null); },
      reset: reset,
      // called after heightSpans has bound its listeners (see init)
      listen: function () {
        root.addEventListener('input', onEvent);
        root.addEventListener('change', onEvent);
        // the theme rewrites #customizer- on every variant change; a new
        // height (or width) field means rebuilt fields, and the old line
        // went with the old ones
        var known = input('max-height-') || input('max-width-');
        var box = input('customizer-');
        if (box && 'MutationObserver' in window) {
          new MutationObserver(function () {
            var now = input('max-height-') || input('max-width-');
            if (now === known) return;
            known = now;
            rebuiltAt = Date.now();
            reset();
          }).observe(box, { childList: true, subtree: true });
        }
        paint();
      }
    };
  }

  /* ---------- height span from the typed height -----------------
     Some products sell their height in spans - "Höjd (intervall)":
     20-60 cm, 61-80 cm, ... - and every span variant carries its own
     limits and price table (custom.custom_products). The page shows one
     height field instead of the span row: pdp-spans.liquid hides the
     row and prints each span's real limits (labels drift from the data).

     Typing a height does what a click on its span did on the old page,
     and nothing more: a height that belongs to another span selects that
     span through the theme's own picker - the hidden radio plus a change
     event - and the theme refetches the section and rebuilds the
     customizer with that span's limits and table. The rebuild empties the
     fields, so what the shopper entered is typed back in afterwards.

     When: at once when the number is complete, as on the old page. A
     whole number that another digit could still turn into a height on
     offer waits - 22 on a plissé that goes to 240 may be the start of 220 -
     until the field is left, or Enter; meanwhile the customizer shows its
     own red, as the old page did for a height outside the chosen span. Its
     decimal does not make it wait (the user, 2026-10-03): 200 switches at
     once and the price follows, and 200,5 switches again when it belongs
     to another span. A switch holds the fields for about half a second, so
     the keys typed meanwhile are kept and go back with the entries (typed,
     below): without that, "130" switched at once and lost its ",5", and a
     timer once ordered a 22 cm blind ("22", a pause, "0" while the fields
     were held). No timer.

     A height between two spans that meet belongs to the nearer of them
     (the user, 2026-10-02): the spans are whole centimetres, 30-80 and
     81-100, so 80,1-80,9 lies in neither. 80,3 goes to 30-80 and 80,7 to
     81-100; halfway, 80,5 goes up, as a measure between two of the
     customizer's size groups is priced as the next group up
     (customizer-decimals.js). The height stays as typed. The customizer's
     own test reads the height field's min and max, which are the span's
     own (30-80, 81-100), so each field is given the room on its side of
     the meeting point: 30-80 takes up to 80,4 and 81-100 from 80,5
     (fitLimits). Its price lookup already prices a measure just outside
     its groups as the nearest group (findPriceForMeasurement, read), so
     80,3 costs what 80 does. A wider gap between two spans is not
     bridged: a height there can be centimetres from either, so the line
     asks the shopper to choose (problem()).

     The customizer is used here only the way a shopper uses it: its
     events run as they always do, it validates and prices every value
     itself, and its prices, labels, buttons, hidden fields and instance
     are never written - of its limits, only the half centimetre at a
     meeting point (fitLimits, above). The contacts with its fields, all
     as a shopper's own actions would be:
     - Just before the switch the width and height are emptied (value plus
       input and change). Every customizer instance ever made keeps
       listening, and 150 ms after any variant change each reads its base
       price back off the price on display (component-customizer.js
       setupVariantChangeListener -> refreshData -> getBasePrice, read,
       not changed). With entries in the fields that price holds them and
       every instance adds them again - 5 727 kr for 1 909 after one
       switch, and after a refetch that failed the next valid height sent
       33 904 kr instead of 2 119 kr to the cart. Emptied fields show the
       plain base. Only width and height feed the price.
     - The field the shopper is in is left before the swap (theme-gotchas
       38: removing a focused, edited field fires its change mid-swap, and
       the new instance inherits a doubled base - 11 264 kr for 1 719 kr,
       seen), and the text and number fields are inert until the entries
       are back: typed into before the new instance exists (100 ms after
       the swap), they would feed it the wrong base too. Vägg/Tak and the
       other radios stay live, so a click on them during the switch counts.
     - SETTLE ms after the rebuild - once the new instance exists and has
       priced its empty fields - the entries are typed back in: the value
       plus the input and change events that typing and leaving the field
       give. The keys the shopper pressed while the fields were held are
       part of the entry they were typed into (typed). A decimal separator
       among them with no digit after it yet cannot be typed back - "200,"
       is no number to a number field - so the next digit typed is put in
       after it, value plus input as typing it would (sepNext). The radios,
       which do not feed the price, go back at once, before the next paint,
       as a click on them would. Only after a switch made here; a colour the
       shopper picks gets the theme's usual fresh fields.

     What cannot be ordered is said in words, on measureNotes' line under
     the width and height (problem(), say()): a height outside the colour's
     range, one between two spans, one whose span this colour lacks or
     cannot sell - the theme's own picker tells, as it renders no value that
     does not exist with the colour and marks one it cannot sell
     class="disabled" - and an update that failed. Such a height is not
     switched to: the page stays on its span, and the customizer's red and
     the line say why. With the span buttons gone, this is how the old
     page's crossed-out pills speak now. A width the new span does not
     allow, once typed back, is measureNotes' own to say. The line is the
     fourth contact: an element of ours inside the customizer's fields
     grid, with its own class and no id.

     A refetch that fails leaves the page as the theme leaves it after a
     failed span click on the old page ("Ej tillgänglig"); the emptied
     width and height keep the price right, and the shopper types them
     again. The sign is the theme's own "unavailable" text on the buy
     button while a switch is out, or FAIL_MS without a rebuild. */
  function heightSpans(root, notes) {
    var dataEl = root.querySelector('[data-pdp-spans]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var index = data.rangeIndex;
    if (typeof index !== 'number' || index < 0) return;

    var id = root.id;
    var variants = (data.variants || []).map(function (v) {
      return {
        id: v.id, options: v.options,
        min: parseFloat(v.min), max: parseFloat(v.max), minText: v.min, maxText: v.max,
        minW: parseFloat(v.minW), maxW: parseFloat(v.maxW)
      };
    }).filter(function (v) { return !isNaN(v.min) && !isNaN(v.max); });
    if (variants.length < 2) return;

    var SETTLE = 350;     // ms after the last rebuild before entries go back
    var GRACE = 1500;     // a later rebuild this soon still belongs to the switch
    var FAIL_MS = 8000;   // no rebuild by then: the refetch is taken as failed
    var saved = {};       // field id, or 'radio:' + name -> the shopper's entry
    var pending = null;   // the switch, queued behind the event that asked for it
    var switching = null; // the span a refetch is on its way to
    var watchdog = null;  // ends the wait for a refetch that never lands
    var settle = null;
    var graceUntil = 0;
    var retries = 0;      // switches made since the shopper last typed
    var focusId = null;   // the customizer field the shopper is in
    var clearing = false; // the width and height are being emptied for a switch
    var failWatch = null;
    var typed = null;     // while a switch holds the fields: the keys for the field the shopper was in
    var sepNext = null;   // field id whose next digit follows a separator typed while held
    var knownInput = heightInput();

    function node(prefix) { return document.getElementById(prefix + id); }
    function box() { return node('customizer-'); }
    function heightInput() { return document.getElementById('max-height-' + id); }

    function heightValue() {
      var input = heightInput();
      return input ? parseFloat(String(input.value).replace(',', '.')) : NaN;
    }

    function currentVariant() {
      var form = node('product-form-');
      var input = form && form.querySelector('input[name="id"]');
      var vid = input ? Number(input.value) : NaN;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === vid) return variants[i];
      }
      return null;
    }

    // the spans that share every other option (colour) with the variant
    function spans(cur) {
      return variants.filter(function (v) {
        return v.options.every(function (value, i) { return i === index || value === cur.options[i]; });
      }).sort(function (a, b) { return a.max - b.max || a.min - b.min; });
    }

    // two spans meet when no more than a centimetre lies between them (80
    // and 81): the first height, in tenths, that belongs to the upper one -
    // the halfway point, which goes up (see the header). null when they do
    // not meet
    function meeting(lower, upper) {
      if (!lower || !upper || upper.min - lower.max > 1) return null;
      return Math.ceil((Math.round(lower.max * 10) + Math.round(upper.min * 10)) / 2);
    }

    // the span of this colour that holds the height, if any. One between
    // two spans that meet belongs to the nearer (80,3 to 30-80, 80,7 to
    // 81-100). One below or above them all, in a span this colour lacks, or
    // in a wider gap has none: problem() says which
    function spanFor(height, cur) {
      if (!(height > 0)) return null;
      var list = spans(cur);
      for (var i = 0; i < list.length; i++) {
        if (list[i].min <= height && height <= list[i].max) return list[i];
      }
      var below = null;
      var above = null;
      list.forEach(function (v) {
        if (v.max < height && (!below || v.max > below.max)) below = v;
        if (v.min > height && (!above || v.min < above.min)) above = v;
      });
      var meet = meeting(below, above);
      if (meet === null) return null;
      return Math.round(height * 10) >= meet ? above : below;
    }

    // the room on the current span's side of a meeting point, so the
    // customizer's own test (validateInputs reads the field's min and max)
    // takes the heights that belong to it: 30-80's field goes to 80,4 and
    // 81-100's starts at 80,5. Only spans that meet get it
    function fitLimits() {
      var input = heightInput();
      var cur = currentVariant();
      if (!input || !cur) return;
      var below = null;
      var above = null;
      spans(cur).forEach(function (v) {
        if (v.max < cur.min && (!below || v.max > below.max)) below = v;
        if (v.min > cur.max && (!above || v.min < above.min)) above = v;
      });
      var low = meeting(below, cur);
      var high = meeting(cur, above);
      if (low !== null && parseFloat(input.getAttribute('min')) !== low / 10) input.setAttribute('min', String(low / 10));
      if (high !== null && parseFloat(input.getAttribute('max')) !== (high - 1) / 10) input.setAttribute('max', String((high - 1) / 10));
    }

    // whether the theme's own picker lets the span be bought with this
    // colour: a value that does not exist with it is not rendered at all, one
    // that is not available carries class="disabled"
    // (product-variant-options.liquid, read)
    function spanOff(value) {
      var radio = spanRadio(value);
      return !radio || radio.disabled || radio.classList.contains('disabled') || !radio.getAttribute('data-variant-id');
    }

    // the span the typed height needs, when it is not the one selected and
    // can be bought
    function dueSpan() {
      var cur = currentVariant();
      var target = cur && spanFor(heightValue(), cur);
      if (!target || target.id === cur.id || spanOff(target.options[index])) return null;
      return target;
    }

    // why the typed height cannot be ordered with this colour - for the line
    // under the fields; null when it can, or when the field is empty
    function problem() {
      var cur = currentVariant();
      var h = heightValue();
      if (!cur || !(h > 0)) return null;
      var list = spans(cur);
      if (!list.length) return null;
      var lo = Math.min.apply(null, list.map(function (v) { return v.min; }));
      var hi = Math.max.apply(null, list.map(function (v) { return v.max; }));
      if (h < lo || h > hi) return { kind: 'range', min: lo, max: hi };
      var span = spanFor(h, cur);
      if (span) {
        return span.id !== cur.id && spanOff(span.options[index]) ? { kind: 'unavailable', span: span.options[index] } : null;
      }
      // none of this colour's spans holds it: one another colour has, or a gap
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].min <= h && h <= variants[i].max) return { kind: 'unavailable', span: variants[i].options[index] };
      }
      var below = null;
      var above = null;
      list.forEach(function (v) {
        if (v.max < h && (below === null || v.max > below)) below = v.max;
        if (v.min > h && (above === null || v.min < above)) above = v.min;
      });
      return { kind: 'gap', height: h, below: below, above: above };
    }

    // the message in the section's own words ([min], [span] ...); the
    // numbers are written the Swedish way by measureNotes (80,5, no ",0")
    function words(p) {
      var cur = currentVariant();
      return notes.format(p.kind, {
        min: p.min, max: p.max, height: p.height, below: p.below, above: p.above,
        span: p.span || '',
        colour: cur ? cur.options.filter(function (o, i) { return i !== index; }).join(' / ') : ''
      });
    }

    // the height's part of measureNotes' line under the width and height
    function say(p) {
      notes.height(p ? words(p) : '');
    }

    // whether this colour's spans differ in width (Mellanglaspersienn): then
    // measureNotes' width message names the height it depends on. A span
    // whose data has no width limits is left out of the count
    function widthVaries() {
      var cur = currentVariant();
      if (!cur) return false;
      var seen = {};
      spans(cur).forEach(function (v) {
        if (!isNaN(v.minW) && !isNaN(v.maxW)) seen[v.minW + '-' + v.maxW] = true;
      });
      return Object.keys(seen).length > 1;
    }

    // a whole number that another digit could still turn into a height on
    // offer (22 on a plissé that goes to 240 may be the start of 220). Not
    // its decimal: 200 switches at once, and a ",5" typed during the switch
    // goes back with the entries (typed)
    function canGrow() {
      var input = heightInput();
      var cur = currentVariant();
      var text = input ? String(input.value) : '';
      if (!cur || !/^\d+$/.test(text)) return false;
      var list = spans(cur);
      var top = Math.max.apply(null, list.map(function (v) { return v.max; }));
      return Number(text) * 10 <= top;
    }

    // the whole height on offer with this colour, drawn over the label's
    // range by the snippet's stylesheet (Liquid prints the first value)
    function paintRange() {
      var cur = currentVariant();
      var list = cur ? spans(cur) : [];
      if (!list.length || !data.rangeText) return;
      var lo = list[0];
      var hi = list[0];
      list.forEach(function (v) {
        if (v.min < lo.min) lo = v;
        if (v.max > hi.max) hi = v;
      });
      var text = data.rangeText.replace('__MIN__', lo.minText).replace('__MAX__', hi.maxText);
      root.style.setProperty('--rd-height-span', '"' + text.replace(/["\\]/g, '') + '"');
    }

    // a switch is on its way: the snippet's stylesheet keeps the height
    // from showing the old span's red for a height another span holds
    function busy(on) {
      root.classList.toggle('rd-span-switching', on);
    }

    // the text and number fields, not the radios (see the header)
    function lockInputs(on) {
      var b = box();
      if (!b) return;
      Array.prototype.forEach.call(b.querySelectorAll('input'), function (el) {
        if (el.type !== 'text' && el.type !== 'number') return;
        if (on) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
    }

    function lock() {
      var b = box();
      if (!b) return;
      var active = document.activeElement;
      // leaving the field fires its change now, while the old span is in
      if (active && b.contains(active) && typeof active.blur === 'function') active.blur();
      lockInputs(true);
    }

    /* What the shopper sees while a span comes in stays as it was. The swap
       shows the span variant's own 0 SEK, then the customizer's "Från ...",
       and empties the fields until the entries go back - on screen that
       reads as the page reloading. So the price is a still copy over the
       live one until the entries are back and priced, and the emptied
       fields show the entries through the stylesheet's field hints, in the
       look of a filled field (rd-span-hold-*, --rd-hold-*). Presentation
       only: the live price changes underneath, unseen, where the customizer
       reads its base as always, and the copy has none of its classes. The
       price and the customizer wrapper are held at their height, so nothing
       below them moves. The radios stay free to click. */
    var still = null;

    function textBox(el) {
      var range = document.createRange();
      range.selectNodeContents(el);
      return range.getBoundingClientRect();
    }

    function hold() {
      if (still) return;
      var price = node('price-');
      var shown = price && price.querySelector('.price__regular .price-item--regular');
      var b = box();
      still = { price: price, box: b, priceMin: price ? price.style.minHeight : '', boxMin: b ? b.style.minHeight : '' };
      if (shown && price.parentNode) {
        var host = price.parentNode;
        var hr = host.getBoundingClientRect();
        var sr = shown.getBoundingClientRect();
        var face = getComputedStyle(shown);
        var copy = document.createElement('span');
        copy.className = 'rd-span-still';
        copy.setAttribute('aria-hidden', 'true');
        copy.textContent = shown.textContent.trim();
        ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'color'].forEach(function (k) { copy.style[k] = face[k]; });
        copy.style.whiteSpace = 'nowrap';
        copy.style.left = (sr.left - hr.left - host.clientLeft) + 'px';
        copy.style.top = (sr.top - hr.top - host.clientTop) + 'px';
        host.classList.add('rd-span-host');
        host.appendChild(copy);
        // the copy is a box as tall as its line height, the price an inline
        // run as tall as its font: line up the text, not the boxes (4 px
        // low otherwise, at 20px type on a 32px line)
        var want = textBox(shown);
        var got = textBox(copy);
        if (want.height && got.height) {
          copy.style.top = (parseFloat(copy.style.top) + want.top - got.top) + 'px';
          copy.style.left = (parseFloat(copy.style.left) + want.left - got.left) + 'px';
        }
        price.style.minHeight = price.getBoundingClientRect().height + 'px';
        price.classList.add('rd-span-held');
        still.copy = copy;
        still.host = host;
      }
      if (b) b.style.minHeight = b.getBoundingClientRect().height + 'px';
      // the entries as the fields showed them: a number field writes 80,5
      [['max-width-', 'w', true], ['max-height-', 'h', true], ['mounting-height-', 'mh', false]].forEach(function (f) {
        var value = saved[f[0] + id];
        if (!value) return;
        var text = f[2] ? String(value).replace('.', ',') : String(value);
        root.style.setProperty('--rd-hold-' + f[1], '"' + text.replace(/["\\]/g, '') + '"');
        root.classList.add('rd-span-hold-' + f[1]);
      });
    }

    function release() {
      if (!still) return;
      if (still.copy && still.copy.parentNode) still.copy.parentNode.removeChild(still.copy);
      if (still.host) still.host.classList.remove('rd-span-host');
      if (still.price) {
        still.price.classList.remove('rd-span-held');
        still.price.style.minHeight = still.priceMin;
      }
      if (still.box) still.box.style.minHeight = still.boxMin;
      ['w', 'h', 'mh'].forEach(function (k) {
        root.classList.remove('rd-span-hold-' + k);
        root.style.removeProperty('--rd-hold-' + k);
      });
      still = null;
    }

    /* Keys while a switch holds the fields. lock() leaves the field the
       shopper is in and the fields are inert, so a key typed meanwhile -
       the ",5" right after "200" - would land on the page and be lost.
       They are kept for that field, in order, and restore() types them
       back as part of its entry. Digits, a separator and Backspace for a
       number field, any character for a text one; Tab, Enter, Escape or a
       click mean the shopper has moved on, and what came before stays. */
    function startTyping() {
      var b = box();
      var active = document.activeElement;
      var pendingSep = sepNext;
      typed = null;
      sepNext = null;
      if (!b || !active || !b.contains(active) || !active.id) return;
      if (active.type !== 'number' && active.type !== 'text') return;
      // a separator still waiting for its digit comes along
      typed = { id: active.id, number: active.type === 'number', keys: pendingSep === active.id ? [','] : [], open: true };
      showTyped();
    }

    // the held field shows the keys as they come, in hold()'s look of a
    // filled field. Presentation only
    function showTyped() {
      if (!typed || !still) return;
      var key = { 'max-width-': 'w', 'max-height-': 'h', 'mounting-height-': 'mh' }[typed.id.slice(0, typed.id.length - id.length)];
      if (!key) return;
      var text = typeInto(saved[typed.id], typed.keys, typed.number);
      if (typed.number) text = text.replace('.', ',');
      if (text) {
        root.style.setProperty('--rd-hold-' + key, '"' + text.replace(/["\\]/g, '') + '"');
        root.classList.add('rd-span-hold-' + key);
      } else {
        root.style.removeProperty('--rd-hold-' + key);
        root.classList.remove('rd-span-hold-' + key);
      }
    }

    function onKey(e) {
      if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      var k = e.key === 'Decimal' ? ',' : e.key;
      if (typed) {
        if (!typed.open) return;
        if (k === 'Tab' || k === 'Enter' || k === 'Escape') { typed.open = false; return; }
        var take = k === 'Backspace' || (typed.number ? /^[0-9.,]$/.test(k) : String(k).length === 1);
        if (!take) return;
        e.preventDefault();
        retries = 0;
        typed.keys.push(k === 'Backspace' ? '\b' : k);
        showTyped();
        return;
      }
      if (!sepNext) return;
      // the digit after a separator restore() could not type back: the field
      // shows "200", and the shopper's next 5 means 200,5
      var field = document.getElementById(sepNext);
      sepNext = null;
      if (!field || e.target !== field) return;
      if (/^[0-9]$/.test(k) && /^\d+$/.test(String(field.value))) {
        e.preventDefault();
        retries = 0;
        field.value = field.value + '.' + k;
        field.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (k === 'Backspace') {
        // the separator it deletes is the one not shown
        e.preventDefault();
      }
    }

    // as a shopper clearing them would; see the header for why
    function clearPriceInputs() {
      clearing = true;
      try {
        ['max-width-', 'max-height-'].forEach(function (prefix) {
          var field = node(prefix);
          if (!field || field.value === '') return;
          field.value = '';
          field.dispatchEvent(new Event('input', { bubbles: true }));
          field.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } finally {
        clearing = false;
      }
    }

    function remember(field) {
      if (field.type === 'hidden') return;
      if (field.type === 'radio' || field.type === 'checkbox') {
        if (field.checked && field.name) saved['radio:' + field.name] = field.value;
      } else if (field.id) {
        saved[field.id] = field.value;
      }
    }

    function spanRadio(value) {
      var picker = node('variant-selects-');
      if (!picker) return null;
      var fieldsets = picker.querySelectorAll('fieldset');
      for (var i = 0; i < fieldsets.length; i++) {
        var legend = fieldsets[i].querySelector(':scope > legend');
        if (!legend || legend.getAttribute('data-legend') !== data.rangeName) continue;
        var radios = fieldsets[i].querySelectorAll('input[type="radio"]');
        for (var j = 0; j < radios.length; j++) {
          if (radios[j].value === value) return radios[j];
        }
      }
      return null;
    }

    // the theme's own sign of a failed refetch: setUnavailable() writes
    // this text on the buy button (global.js, read)
    function watchFailure() {
      unwatchFailure();
      var form = node('product-form-');
      var label = form && form.querySelector('[name="add"] > span');
      var word = window.variantStrings && window.variantStrings.unavailable;
      if (!label || !word || !('MutationObserver' in window)) return;
      failWatch = new MutationObserver(function () {
        if (switching && label.textContent.trim() === String(word).trim()) failed();
      });
      failWatch.observe(label, { childList: true, characterData: true, subtree: true });
    }
    function unwatchFailure() {
      if (failWatch) failWatch.disconnect();
      failWatch = null;
    }

    function failed() {
      if (!switching) return;
      unwatchFailure();
      clearTimeout(watchdog);
      clearTimeout(settle);
      switching = null;
      // the fields stay empty: the shopper types the whole entry again
      typed = null;
      busy(false);
      lockInputs(false);
      release();
      say({ kind: 'failed' });
      // the width and height were emptied for the switch
      notes.refresh();
    }

    function go() {
      pending = null;
      if (switching) return; // the rebuild on its way checks again
      var target = dueSpan();
      // two tries: a span the theme does not select is left to the customizer.
      // No switch after all: measureNotes judges what the event left
      if (!target || retries >= 2) { busy(false); notes.refresh(); return; }
      var radio = spanRadio(target.options[index]);
      if (!radio) { busy(false); notes.refresh(); return; }
      retries += 1;
      switching = target; // before lock(): its change event must not start another
      clearTimeout(watchdog);
      watchdog = setTimeout(failed, FAIL_MS);
      // in this order: what is on screen is held, the field is left while
      // the old span is in (its keys kept from here), then the plain base
      // goes on display, then the span is picked
      hold();
      startTyping();
      lock();
      clearPriceInputs();
      watchFailure();
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function onEntry(e) {
      if (clearing) return;
      var field = e.target;
      var b = box();
      if (!field || field.tagName !== 'INPUT' || !b || !b.contains(field)) return;
      if (e.isTrusted) {
        retries = 0;
        // the shopper changed the field another way (a paste, leaving it)
        sepNext = null;
      }
      remember(field);
      if (switching || field !== heightInput()) return;
      // a number that may still grow waits while it is being typed, and says
      // nothing yet (on the way to 250, "25" is below every span); left, or
      // with Enter, it goes as it is
      var growing = e.type === 'input' && canGrow();
      var p = growing ? null : problem();
      say(p);
      if (growing || p || !dueSpan()) {
        clearTimeout(pending);
        pending = null;
        busy(false);
        return;
      }
      // queued already (restore() fires input, then change): keep it
      if (pending) return;
      busy(true);
      // after this event, so the customizer has taken it first
      pending = setTimeout(go, 0);
    }

    // The shopper's Vägg/Tak, Vänster/Höger and roll direction, back in the
    // rebuilt fields as clicking them would. The swap brings the defaults;
    // these are form fields only - the customizer prices width and height
    // alone (isCustomizerInput, read) - so they go back as soon as the new
    // fields are in, before the next paint, rather than after SETTLE.
    // Radios only: a checkbox may carry a price (the gardin page's sy upp)
    // and goes back with the entries in restore()
    function radiosBack() {
      var b = box();
      if (!b) return;
      b.querySelectorAll('input[type="radio"]').forEach(function (field) {
        var value = saved['radio:' + field.name];
        if (value === undefined || field.value !== value || field.checked) return;
        field.checked = true;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // the shopper's entries into the rebuilt fields, as typing them would
    function restore() {
      settle = null;
      switching = null;
      clearTimeout(watchdog);
      unwatchFailure();
      graceUntil = Date.now() + GRACE;
      busy(false);
      lockInputs(false);
      var b = box();
      if (!b) { typed = null; release(); return; }
      var active = document.activeElement;
      var refocus = focusId && (!active || active === document.body) ? focusId : null;
      // the keys pressed while the fields were held belong to the entry they
      // were typed into. A separator with no digit yet cannot go in a number
      // field ("200." reads as empty): it waits for the digit (sepNext)
      var waiting = null;
      if (typed) {
        if (typed.keys.length) {
          var text = typeInto(saved[typed.id], typed.keys, typed.number);
          if (typed.number && text.slice(-1) === '.') {
            text = text.slice(0, -1);
            waiting = typed.id;
          }
          saved[typed.id] = text;
        }
        typed = null;
      }
      radiosBack();
      var touched = [];
      b.querySelectorAll('input').forEach(function (field) {
        if (field.type === 'hidden' || field.type === 'radio') return;
        if (field.type === 'checkbox') {
          var value = saved['radio:' + field.name];
          if (value !== undefined && field.value === value && !field.checked) {
            field.checked = true;
            touched.push(field);
          }
        } else if (field.id && saved[field.id] && field.value !== saved[field.id]) {
          field.value = saved[field.id];
          touched.push(field);
        }
      });
      touched.forEach(function (field) {
        if (field.type !== 'checkbox') field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      // the customizer has repriced them synchronously: what the page shows
      // now is final, and the still copy and the held look can go
      release();
      // the rebuild took the field from under the shopper's cursor
      if (refocus) {
        var again = document.getElementById(refocus);
        if (again && b.contains(again)) {
          try { again.focus({ preventScroll: true }); } catch (err) { /* a courtesy only */ }
        }
      }
      // only where the shopper's next key lands in that field
      sepNext = waiting && document.activeElement === document.getElementById(waiting) ? waiting : null;
    }

    function rebuilt() {
      paintRange();
      // the rebuild took the message line with the old fields
      say(null);
      if (switching || Date.now() < graceUntil) {
        // the new fields are inert as the old ones were, until the entries are back
        if (switching) lockInputs(true);
        radiosBack();
        clearTimeout(settle);
        settle = setTimeout(restore, SETTLE);
        return;
      }
      // the shopper's own variant change: the theme's usual fresh fields
      saved = {};
      typed = null;
      sepNext = null;
      busy(false);
      release();
    }

    // measureNotes leaves the height to this code, and holds its own judgement
    // while a switch is queued or out (the fields are emptied and typed back)
    notes.attach({
      busy: function () { return !!switching || !!pending; },
      widthVaries: widthVaries
    });
    root.addEventListener('input', onEntry);
    root.addEventListener('change', onEntry);
    // the same rule for every variant change, not only this code's: a pill
    // or chip click has already taken the focus, but the theme's own
    // follow-up change (autoSelectFirstAvailableOptions) can come while the
    // shopper is back in a field. Capture, so it runs before the theme's
    // handler on the same element starts the refetch
    var picker = node('variant-selects-');
    if (picker) {
      picker.addEventListener('change', function () {
        var b = box();
        var active = document.activeElement;
        if (b && active && b.contains(active) && typeof active.blur === 'function') active.blur();
      }, true);
    }
    document.addEventListener('focusin', function (e) {
      var b = box();
      focusId = b && b.contains(e.target) && e.target.id ? e.target.id : null;
    });
    // a tap outside the fields: the shopper has moved on, no focus comes back.
    // Any tap ends the keys kept during a switch (what came before stays) and
    // a separator's wait for its digit
    document.addEventListener('pointerdown', function (e) {
      var b = box();
      if (!b || !b.contains(e.target)) focusId = null;
      if (typed) typed.open = false;
      sepNext = null;
    }, true);
    // capture: before the page's own handlers, and while the fields are inert
    // the keys arrive on the page, not in a field
    document.addEventListener('keydown', onKey, true);

    // A span variant without limits (a data error: Cord Control creme's
    // Cream/Grå 161-180 had no metaobject and cost 0 kr) gets no height
    // field, so nothing here could lead away from it - the row comes back
    // for as long as it is selected, and the shopper picks as on the old page
    function fallback() {
      var on = !currentVariant();
      if (on === root.classList.contains('rd-span-pills')) return;
      root.classList.toggle('rd-span-pills', on);
      // hidden, the row's pills were left out of segmentWidth's measure
      root.dispatchEvent(new CustomEvent('pdp:measure'));
    }

    // the theme rewrites #customizer- on every variant change; a new height
    // field is the sign it was rebuilt rather than merely repriced
    var b = box();
    if (b && 'MutationObserver' in window) {
      new MutationObserver(function () {
        fallback();
        var input = heightInput();
        if (!input || input === knownInput) return;
        knownInput = input;
        fitLimits();
        rebuilt();
      }).observe(b, { childList: true, subtree: true });
    }

    fallback();
    fitLimits();
    paintRange();
    say(null);
  }

  PDP.measure = function (root) {
    var notes = measureNotes(root);
    heightSpans(root, notes);
    // after heightSpans, on purpose: its listeners must run first, so that a
    // span switch it queues on an event is known (busy) when measureNotes
    // judges that same event. Reordered, the width would be judged against
    // the span about to be left
    notes.listen();
  };
})();
